/**
 * Admin Edit Game Image Page
 * Dedicated page for managing game images with upload, preview, and transformation
 */

import React, { useState, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Upload, X, Check, Loader2 } from "lucide-react";
import type { FirestoreGame } from "@/../../server/db-games-firestore";

interface ImagePreview {
  url: string;
  file: File;
  size: number;
  dimensions?: { width: number; height: number };
}

export default function AdminEditGameImage() {
  const { gameId } = useParams<{ gameId: string }>();
  const [, navigate] = useLocation();
  // Simple notification function
  const notify = (title: string, message: string, type: 'success' | 'error' = 'success') => {
    console.log(`[${type.toUpperCase()}] ${title}: ${message}`);
    // Could be replaced with a toast library later
  };

  // State
  const [preview, setPreview] = useState<ImagePreview | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Queries
  const { data: game, isLoading: gameLoading } = trpc.admin.gamesFirestore.getGame.useQuery(
    { id: gameId || "" },
    { enabled: !!gameId }
  );

  // Mutations
  const uploadImageMutation = trpc.admin.gamesFirestore.uploadImage.useMutation();
  const updateImageMutation = trpc.admin.gamesFirestore.updateGame.useMutation({
    onSuccess: () => {
      notify("Success", "Game image updated successfully", "success");
      setPreview(null);
      navigate(`/admin/games/${gameId}`);
    },
    onError: (error: any) => {
      notify("Error", error.message || "Failed to update game image", "error");
    },
  });

  // Handlers
  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      notify("Invalid file", "Please select an image file (JPG, PNG, WebP, etc.)", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      notify("File too large", "Image must be less than 5MB", "error");
      return;
    }

    try {
      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        setPreview({
          url,
          file,
          size: file.size,
          dimensions: { width: img.width, height: img.height },
        });
      };

      img.onerror = () => {
        notify("Invalid image", "Could not load image. Please try another file.", "error");
      };

      img.src = url;
    } catch (error) {
      notify("Error", "Failed to process image", "error");
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!preview || !gameId) return;

    setIsUploading(true);
    try {
      // Upload image to Firebase Storage
      const uploadResult = await uploadImageMutation.mutateAsync({
        gameId,
        imageBuffer: await preview.file.arrayBuffer().then(ab => Buffer.from(ab)),
        filename: preview.file.name,
        contentType: preview.file.type || "image/jpeg",
      });

      if (uploadResult.success && uploadResult.url) {
        // Update game with new image URL
        await updateImageMutation.mutateAsync({
          id: gameId,
          image: uploadResult.url,
        });
        notify("Success", "Game image updated successfully", "success");
        setPreview(null);
        navigate(`/admin/games/${gameId}`);
      }
    } catch (error) {
      notify("Error", error instanceof Error ? error.message : "Failed to upload image", "error");
      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
    }
  };

  if (gameLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="container py-8">
        <Card className="p-6 border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">Game not found</p>
          </div>
        </Card>
      </div>
    );
  }

  const gameData = game as FirestoreGame | undefined;

  return (
    <div className="container py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold">Edit Game Image</h1>
          <Button variant="outline" onClick={() => navigate(`/admin/games/${gameId}`)}>
            Back to Game
          </Button>
        </div>
        <p className="text-muted-foreground">
          Updating image for: <strong>{game.name}</strong>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Current Image */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Current Image</h2>
          {game.image ? (
            <div className="space-y-4">
              <img
                src={game.image}
                alt={game.name}
                className="w-full h-64 object-cover rounded-lg border border-border"
              />
              <div className="text-sm text-muted-foreground">
                <p>Current image is set</p>
              </div>
            </div>
          ) : (
            <div className="h-64 bg-muted rounded-lg flex items-center justify-center border-2 border-dashed border-border">
              <p className="text-muted-foreground">No image set</p>
            </div>
          )}
        </Card>

        {/* Upload Area */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Upload New Image</h2>

          {!preview ? (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm font-medium mb-2">Drag and drop your image here</p>
              <p className="text-xs text-muted-foreground mb-4">or click to select</p>
              <p className="text-xs text-muted-foreground">
                Supported formats: JPG, PNG, WebP (max 5MB)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleFileSelect(e.target.files[0]);
                  }
                }}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="w-full h-64 object-cover rounded-lg border border-border"
                />
                <button
                  onClick={() => setPreview(null)}
                  className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Image Info */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">File name:</span>
                  <span className="font-medium">{preview.file.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">File size:</span>
                  <span className="font-medium">
                    {(preview.size / 1024).toFixed(2)} KB
                  </span>
                </div>
                {preview.dimensions && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Dimensions:</span>
                    <span className="font-medium">
                      {preview.dimensions.width} × {preview.dimensions.height}px
                    </span>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="w-full"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Upload Image
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                onClick={() => setPreview(null)}
                disabled={isUploading}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Guidelines */}
      <Card className="mt-8 p-6 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-3">Image Guidelines</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>• Recommended size: 400×600px (portrait) or 600×400px (landscape)</li>
          <li>• File format: JPG, PNG, or WebP</li>
          <li>• Maximum file size: 5MB</li>
          <li>• Image will be optimized and cached for fast loading</li>
          <li>• High-quality images improve user experience</li>
        </ul>
      </Card>
    </div>
  );
}
